import React, { FC } from 'react';

import { is, isEmpty } from 'ramda';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useRouteMatch } from 'react-router';

import { COMMON } from 'translations';

import { LA_POSTE_SHARE_IMAGE } from '../../../helpers/assets';
import { getLocaleFull } from '../../../helpers/i18n';
import {
  getAlternateLanguages,
  getCanonical,
  getDescription,
  getImage,
  getIndexation,
  getTitle,
} from '../../../helpers/seo';

const getSuffixedTitle = getTitle();

const getMetaImage: any = (image: any) => {
  if (is(Object, image) && !isEmpty(image) && !isEmpty(image.url)) {
    return image;
  }

  return {
    url: LA_POSTE_SHARE_IMAGE,
    dimensions: {
      height: 630,
      width: 1200,
    },
  };
};

type AlternateLinkType = {
  disabled?: boolean;
  locale?: string;
  to?: string;
};

export const Metas: FC<MetasProps> = ({ document }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const match = useRouteMatch();
  const locale = i18n.language;

  const alternates: {
    [key: string]: AlternateLinkType;
  } = getAlternateLanguages(document, {
    match,
    location,
  });
  const canonical = getCanonical(locale, document, {
    match,
    location,
  });
  const title = getSuffixedTitle(document);
  const description = getDescription(document);
  const image = getImage(document);
  const indexation = getIndexation(document);
  const metaImage = getMetaImage(image);
  const metaImageUrl = metaImage?.src;
  const metaImageHeight = metaImage?.dimensions?.height;
  const metaImageWidth = metaImage?.dimensions?.width;

  const renderAlternate = (key: string): JSX.Element => {
    const alternate = alternates?.[key];

    if (!alternate || !alternate.to || alternate.disabled) {
      return null;
    }

    return (
      <link
        key={key}
        href={alternate.to}
        rel="alternate"
        hrefLang={alternate.locale}
      />
    );
  };

  return (
    <Helmet htmlAttributes={{ locale }}>
      <title lang={locale}>{title}</title>
      <meta name="robots" content={indexation} />
      {description && <meta name="description" content={description} />}
      {Object.keys(alternates).map(renderAlternate)}
      {canonical && <link href={canonical} rel="canonical" />}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical || undefined} />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:site_name" content={t(COMMON.SITE_NAME)} />
      <meta property="og:locale" content={getLocaleFull(locale)} />
      <meta property="og:image" content={metaImageUrl} />
      {metaImageWidth && (
        <meta property="og:image:width" content={metaImageWidth} />
      )}
      {metaImageHeight && (
        <meta property="og:image:height" content={metaImageHeight} />
      )}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@GroupeLaPoste" />
      <meta name="twitter:url" content={canonical || undefined} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={metaImageUrl} />
    </Helmet>
  );
};

type MetasProps = {
  document: any;
};
